import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

export default function Employees() {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "employee" });

  const load = () => api.get("/employees").then((r) => setEmployees(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/employees", form);
      toast.success(`Account created for ${form.name}`);
      setForm({ name: "", email: "", password: "", role: "employee" });
      load();
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  const toggleActive = async (emp, active) => {
    try {
      await api.patch(`/employees/${emp.id}`, { active });
      toast.success(`${emp.name} ${active ? "activated" : "deactivated"}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="employees-page" className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-[#1B2A4A]">Employees</h1>
        <p className="text-sm text-[#475569] mt-1">Team accounts with data-entry activity tracking</p>
      </div>

      <form onSubmit={add} className="bg-white border border-[#E2E8F0] rounded-xl p-5 flex items-end gap-3 flex-wrap">
        <div className="space-y-1.5">
          <Label>Name</Label>
          <Input data-testid="new-employee-name" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="w-48" />
        </div>
        <div className="space-y-1.5">
          <Label>Email</Label>
          <Input data-testid="new-employee-email" required type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="w-56" />
        </div>
        <div className="space-y-1.5">
          <Label>Password</Label>
          <Input data-testid="new-employee-password" required type="text" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} className="w-44" />
        </div>
        <div className="space-y-1.5">
          <Label>Role</Label>
          <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
            <SelectTrigger data-testid="new-employee-role" className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="employee">Employee</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button data-testid="add-employee-button" type="submit" className="bg-[#F5961B] hover:bg-[#dd8512] text-white">
          <Plus size={15} className="mr-1.5" /> Create Account
        </Button>
      </form>

      <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
        <table className="w-full text-sm" data-testid="employees-table">
          <thead>
            <tr className="border-b border-[#E2E8F0] bg-[#F5F7FA] text-left">
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">Name</th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">Email</th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">Role</th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">Joined</th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">Active</th>
            </tr>
          </thead>
          <tbody>
            {employees.map((e) => (
              <tr key={e.id} data-testid={`employee-row-${e.id}`} className="border-b border-[#F1F5F9]">
                <td className="px-4 py-3 font-semibold text-[#0F172A]">{e.name}{e.id === user?.id && <span className="text-xs text-[#94A3B8] font-normal"> (you)</span>}</td>
                <td className="px-4 py-3 text-[#475569]">{e.email}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${
                    e.role === "admin" ? "text-[#1B2A4A] bg-[#FDF0DC] border-[#F5961B]" : "text-[#1D4ED8] bg-[#DBEAFE] border-[#BFDBFE]"
                  }`} data-testid={`employee-role-${e.id}`}>{e.role}</span>
                </td>
                <td className="px-4 py-3 text-[#475569]">{new Date(e.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3">
                  <Switch
                    data-testid={`employee-active-${e.id}`}
                    checked={e.active}
                    disabled={e.id === user?.id}
                    onCheckedChange={(v) => toggleActive(e, v)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
